import React, { useRef } from 'react'
import { motion } from 'framer-motion'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'
import {
  Download,
  FileText,
  AlertTriangle,
  Activity,
  Zap,
  Cpu,
  Wifi,
  MapPin,
  TrendingDown,
  CheckCircle2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { exportToPDF } from '../utils/exportPDF'
import { exportToDOCX } from '../utils/exportDOCX'

const TELTRONIC_LOGO = 'https://static.lumi.new/e7/e75944237c715af0b8f2c42d1e5578cc.png'

const incidentDistribution = [
  { name: 'ERBs (Pontos de Operação)', value: 52, percent: 65 },
  { name: 'Outros Locais', value: 28, percent: 35 },
]

const topSites = [
  { name: '14ª DP', incidents: 12 },
  { name: 'CEPOM PMDF (17ª DP)', incidents: 11 },
  { name: '16º BPM (18ª DP)', incidents: 10 },
  { name: 'Outros Sites', incidents: 47 },
]

const linkFailures = [
  { local: '14ª DP', falhas: 8 },
  { local: 'CEPOM PMDF', falhas: 7 },
  { local: '16º BPM', falhas: 6 },
  { local: '5ª DP', falhas: 4 },
  { local: '23ª DP', falhas: 3 },
]

const energyFailures = [
  { local: '14ª DP', falhas: 5 },
  { local: '17ª DP', falhas: 4 },
  { local: '18ª DP', falhas: 4 },
  { local: '12ª DP', falhas: 3 },
  { local: '8ª DP', falhas: 2 },
]

const hardwareFailures = [
  { local: '14ª DP', falhas: 4 },
  { local: 'CEPOM PMDF', falhas: 3 },
  { local: '16º BPM', falhas: 3 },
  { local: '21ª DP', falhas: 2 },
  { local: '7ª DP', falhas: 2 },
]

const rootCauses = [
  { name: 'Falha de Link (Rede Transporte)', value: 28, color: '#3B82F6' },
  { name: 'Falha de Energia Elétrica', value: 22, color: '#F59E0B' },
  { name: 'Falha de Hardware', value: 18, color: '#EF4444' },
  { name: 'Outras Causas', value: 12, color: '#6B7280' },
]

const COLORS_PIE = ['#003366', '#60A5FA']

export default function PresentationPCDF() {
  const presentationRef = useRef<HTMLDivElement>(null)

  const handleExportPDF = async () => {
    const loadingToast = toast.loading('Gerando PDF...')
    try {
      await exportToPDF(presentationRef.current)
      toast.success('PDF gerado com sucesso!', { id: loadingToast })
    } catch (error) {
      toast.error('Erro ao gerar PDF', { id: loadingToast })
      console.error(error)
    }
  }

  const handleExportDOCX = async () => {
    const loadingToast = toast.loading('Gerando DOCX...')
    try {
      await exportToDOCX()
      toast.success('DOCX gerado com sucesso!', { id: loadingToast })
    } catch (error) {
      toast.error('Erro ao gerar DOCX', { id: loadingToast })
      console.error(error)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200">
      {/* Action Bar */}
      <div className="sticky top-0 z-50 bg-white shadow-md border-b border-slate-200 px-4 sm:px-8 py-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <img src={TELTRONIC_LOGO} alt="Teltronic" className="h-10" />
          <div>
            <h1 className="text-sm font-bold text-slate-800">Apresentação de Indicadores</h1>
            <p className="text-xs text-slate-500">Cliente: PCDF | Fonte: GLPI</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm transition-colors shadow-sm"
          >
            <FileText size={16} />
            Exportar PDF
          </button>
          <button
            onClick={handleExportDOCX}
            className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-lg font-medium text-sm transition-colors shadow-sm"
          >
            <Download size={16} />
            Exportar DOCX
          </button>
        </div>
      </div>

      {/* Presentation Content */}
      <div ref={presentationRef} className="max-w-6xl mx-auto px-4 sm:px-8 py-8 space-y-8">
        {/* Slide 1 - Cover */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 rounded-2xl shadow-2xl p-12 text-white relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full -translate-y-32 translate-x-32 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full translate-y-48 -translate-x-48 blur-3xl" />

          <div className="relative z-10">
            <div className="bg-white inline-block p-4 rounded-xl mb-8 shadow-lg">
              <img src={TELTRONIC_LOGO} alt="Teltronic" className="h-16" />
            </div>
            <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
              Indicadores de Incidentes
            </h1>
            <h2 className="text-2xl md:text-3xl font-light text-blue-200 mb-8">
              Cliente: PCDF — Polícia Civil do Distrito Federal
            </h2>
            <div className="flex flex-wrap gap-6 text-sm text-blue-100 mt-12">
              <div className="flex items-center gap-2">
                <Activity size={18} />
                <span>Fonte: Ferramenta GLPI</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText size={18} />
                <span>Período: Análise Histórica</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={18} />
                <span>Total: 80 Registros</span>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Slide 2 - Visão Geral */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
        >
          <div className="border-l-4 border-blue-700 pl-4 mb-8">
            <h2 className="text-3xl font-bold text-slate-800">Visão Geral dos Incidentes</h2>
            <p className="text-slate-500 mt-1">Distribuição por tipo de localidade</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="bg-blue-50 border-l-4 border-blue-700 rounded-r-lg p-6 mb-4">
                <p className="text-5xl font-black text-blue-900">65%</p>
                <p className="text-slate-700 mt-2 text-sm">
                  dos incidentes ocorreram nos <strong>pontos de operação (ERBs)</strong>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <p className="text-3xl font-bold text-slate-800">80</p>
                  <p className="text-xs text-slate-500">Total de Incidentes</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <p className="text-3xl font-bold text-blue-700">52</p>
                  <p className="text-xs text-slate-500">Incidentes em ERBs</p>
                </div>
              </div>
              <div className="mt-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 flex items-start gap-3">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-sm font-semibold text-amber-900">Falhas Massivas</p>
                  <p className="text-xs text-amber-800 mt-1">
                    <strong>3 incidentes</strong> afetaram múltiplos pontos de operação simultaneamente.
                  </p>
                </div>
              </div>
            </div>

            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={incidentDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent }) => `${percent}%`}
                    outerRadius={110}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {incidentDistribution.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS_PIE[index % COLORS_PIE.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.section>

        {/* Slide 3 - Top Sites */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
        >
          <div className="border-l-4 border-blue-700 pl-4 mb-8">
            <h2 className="text-3xl font-bold text-slate-800">Locais Mais Afetados</h2>
            <p className="text-slate-500 mt-1">Top 3 sites com maior número de incidentes</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bg-gradient-to-br from-red-500 to-red-700 rounded-xl p-6 text-white shadow-lg">
              <MapPin size={28} className="mb-3 opacity-80" />
              <p className="text-sm font-medium opacity-90">1º Lugar</p>
              <p className="text-2xl font-bold mt-1">14ª DP</p>
              <p className="text-4xl font-black mt-2">12</p>
              <p className="text-xs opacity-80">incidentes</p>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-700 rounded-xl p-6 text-white shadow-lg">
              <MapPin size={28} className="mb-3 opacity-80" />
              <p className="text-sm font-medium opacity-90">2º Lugar</p>
              <p className="text-2xl font-bold mt-1">CEPOM PMDF (17ª DP)</p>
              <p className="text-4xl font-black mt-2">11</p>
              <p className="text-xs opacity-80">incidentes</p>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-amber-700 rounded-xl p-6 text-white shadow-lg">
              <MapPin size={28} className="mb-3 opacity-80" />
              <p className="text-sm font-medium opacity-90">3º Lugar</p>
              <p className="text-2xl font-bold mt-1">16º BPM (18ª DP)</p>
              <p className="text-4xl font-black mt-2">10</p>
              <p className="text-xs opacity-80">incidentes</p>
            </div>
          </div>

          <div className="h-80 bg-slate-50 rounded-xl p-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topSites}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="incidents" fill="#003366" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.section>

        {/* Slide 4 - Causas Principais */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
        >
          <div className="border-l-4 border-blue-700 pl-4 mb-8">
            <h2 className="text-3xl font-bold text-slate-800">Causadores de Indisponibilidade</h2>
            <p className="text-slate-500 mt-1">Principais problemas reportados ao cliente PCDF</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="border-2 border-blue-200 rounded-xl p-6 bg-blue-50">
              <Wifi className="text-blue-700 mb-3" size={32} />
              <h3 className="font-bold text-slate-800 text-lg">Falha de Link</h3>
              <p className="text-xs text-slate-600 mt-1">Rede de Transporte</p>
              <p className="text-3xl font-black text-blue-700 mt-3">28</p>
            </div>
            <div className="border-2 border-amber-200 rounded-xl p-6 bg-amber-50">
              <Zap className="text-amber-600 mb-3" size={32} />
              <h3 className="font-bold text-slate-800 text-lg">Falha Elétrica</h3>
              <p className="text-xs text-slate-600 mt-1">Energia / Concessionária</p>
              <p className="text-3xl font-black text-amber-600 mt-3">22</p>
            </div>
            <div className="border-2 border-red-200 rounded-xl p-6 bg-red-50">
              <Cpu className="text-red-600 mb-3" size={32} />
              <h3 className="font-bold text-slate-800 text-lg">Falha de Hardware</h3>
              <p className="text-xs text-slate-600 mt-1">Equipamentos / Infra</p>
              <p className="text-3xl font-black text-red-600 mt-3">18</p>
            </div>
          </div>

          <div className="h-72 bg-slate-50 rounded-xl p-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rootCauses} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {rootCauses.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.section>

        {/* Slide 5 - Indisponibilidade por Link */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
        >
          <div className="border-l-4 border-blue-500 pl-4 mb-8 flex items-center gap-3">
            <Wifi className="text-blue-700" size={32} />
            <div>
              <h2 className="text-3xl font-bold text-slate-800">Falhas no Link por Localidade</h2>
              <p className="text-slate-500 mt-1">Indisponibilidade por falha na rede de transporte</p>
            </div>
          </div>

          <div className="h-80 bg-slate-50 rounded-xl p-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={linkFailures} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" />
                <YAxis dataKey="local" type="category" tick={{ fontSize: 12 }} width={120} />
                <Tooltip />
                <Bar dataKey="falhas" fill="#3B82F6" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.section>

        {/* Slide 6 - Indisponibilidade Elétrica */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
        >
          <div className="border-l-4 border-amber-500 pl-4 mb-8 flex items-center gap-3">
            <Zap className="text-amber-600" size={32} />
            <div>
              <h2 className="text-3xl font-bold text-slate-800">Falhas Elétricas por Localidade</h2>
              <p className="text-slate-500 mt-1">Indisponibilidade por falha de energia elétrica</p>
            </div>
          </div>

          <div className="h-80 bg-slate-50 rounded-xl p-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={energyFailures} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" />
                <YAxis dataKey="local" type="category" tick={{ fontSize: 12 }} width={120} />
                <Tooltip />
                <Bar dataKey="falhas" fill="#F59E0B" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.section>

        {/* Slide 7 - Hardware */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
        >
          <div className="border-l-4 border-red-500 pl-4 mb-8 flex items-center gap-3">
            <Cpu className="text-red-600" size={32} />
            <div>
              <h2 className="text-3xl font-bold text-slate-800">Falhas de Hardware por Localidade</h2>
              <p className="text-slate-500 mt-1">Principais localidades com falha de equipamentos</p>
            </div>
          </div>

          <div className="h-80 bg-slate-50 rounded-xl p-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hardwareFailures} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" />
                <YAxis dataKey="local" type="category" tick={{ fontSize: 12 }} width={120} />
                <Tooltip />
                <Bar dataKey="falhas" fill="#EF4444" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.section>

        {/* Slide 8 - Recomendações */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-2xl shadow-2xl p-8 md:p-12 text-white"
        >
          <div className="border-l-4 border-cyan-400 pl-4 mb-8">
            <h2 className="text-3xl font-bold">Recomendações de Melhoria</h2>
            <p className="text-blue-200 mt-1">Sugestões para mitigar a continuidade dos principais incidentes</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Wifi className="text-cyan-400" size={24} />
                <h3 className="font-bold text-lg">Redundância de Link</h3>
              </div>
              <p className="text-sm text-blue-100">
                Implementar redundância de operadora nos pontos críticos (14ª DP, CEPOM, 16º BPM) com failover automático para reduzir indisponibilidade por falha na rede de transporte.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Zap className="text-amber-300" size={24} />
                <h3 className="font-bold text-lg">Backup de Energia</h3>
              </div>
              <p className="text-sm text-blue-100">
                Instalar/expandir nobreaks e geradores nas ERBs com maior incidência de falha elétrica, com monitoramento remoto da autonomia das baterias.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Cpu className="text-red-300" size={24} />
                <h3 className="font-bold text-lg">Renovação de Hardware</h3>
              </div>
              <p className="text-sm text-blue-100">
                Mapear equipamentos com falhas recorrentes e elaborar plano de substituição preventiva. Manter spare parts em locais estratégicos para redução do MTTR.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Activity className="text-green-300" size={24} />
                <h3 className="font-bold text-lg">Monitoramento Proativo</h3>
              </div>
              <p className="text-sm text-blue-100">
                Aprimorar a observabilidade nas ERBs com alertas preditivos via SNMP/Zabbix, antecipando falhas antes que se tornem incidentes.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <AlertTriangle className="text-orange-300" size={24} />
                <h3 className="font-bold text-lg">Plano contra Falhas Massivas</h3>
              </div>
              <p className="text-sm text-blue-100">
                Investigar a causa-raiz dos 3 incidentes de falha massiva e elaborar runbook específico para isolar impacto e acelerar a recuperação multi-site.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <TrendingDown className="text-cyan-300" size={24} />
                <h3 className="font-bold text-lg">Foco nos Top 3 Sites</h3>
              </div>
              <p className="text-sm text-blue-100">
                Priorizar visita técnica nos sites mais afetados (14ª DP, CEPOM, 16º BPM) para identificar fatores ambientais e infraestruturais comuns.
              </p>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20 text-center">
            <p className="text-blue-200 text-sm">
              Apresentação elaborada pela <strong>Teltronic</strong> · Cliente: PCDF · Fonte: GLPI
            </p>
          </div>
        </motion.section>
      </div>
    </div>
  )
}
